import { useNavigate, useSearch } from "@tanstack/react-router";
import { FiGrid, FiList } from "react-icons/fi";
import { useCurrentBoardSlug } from "@/shared/hooks/useCurrentBoardSlug";
import { cn } from "@/shared/lib";

export type CatalogView = "list" | "catalog";

/**
 * Catalog / List view 切替トグル (PC のみ表示)。
 * 状態は `?view=list|catalog` query 経由で URL に保持される。
 *
 * 注: mobile では catalog 固定運用なので呼ばない (CatalogToolbar 側で
 *     device 判定済み)。URL の `?view=list` は preserve するが UI は catalog 表示。
 */
export const ViewModeToggle: React.FunctionComponent = () => {
  const search = useSearch({ from: "/$boardSlug/" }) as { view?: CatalogView };
  const view: CatalogView = search.view === "list" ? "list" : "catalog";
  const boardSlug = useCurrentBoardSlug();
  const navigate = useNavigate();

  const setView = (next: CatalogView): void => {
    navigate({
      to: "/$boardSlug",
      params: { boardSlug },
      search: { view: next },
      replace: true,
    }).catch(console.warn);
  };

  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden">
      <button
        type="button"
        onClick={(): void => setView("catalog")}
        className={cn(
          "flex items-center gap-1 px-3 py-1.5 text-sm transition-colors",
          view === "catalog"
            ? "bg-primary text-primary-foreground"
            : "bg-card text-foreground hover:bg-muted",
        )}
        aria-label="カタログ表示"
        aria-pressed={view === "catalog"}
      >
        <FiGrid size={16} />
        カタログ
      </button>
      <button
        type="button"
        onClick={(): void => setView("list")}
        className={cn(
          "flex items-center gap-1 px-3 py-1.5 text-sm transition-colors border-l border-border",
          view === "list"
            ? "bg-primary text-primary-foreground"
            : "bg-card text-foreground hover:bg-muted",
        )}
        aria-label="リスト表示"
        aria-pressed={view === "list"}
      >
        <FiList size={16} />
        リスト
      </button>
    </div>
  );
};
